/**
 * Authority Landing Page
 *
 * Displays authority information and allows the citizen to select a process or start submission.
 */

import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Verified as VerifiedIcon,
  ArrowForward as ArrowForwardIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Language as WebsiteIcon,
  Description as ProcessIcon,
} from '@mui/icons-material';
import { getAuthorityExtendedInfo, getProcessesForAuthority } from '../api/citizenApi';

/**
 * Format fingerprint for display (groups of 4)
 */
function formatFingerprint(fingerprint: string): string {
  const groups: string[] = [];
  for (let i = 0; i < fingerprint.length; i += 4) {
    groups.push(fingerprint.slice(i, i + 4).toUpperCase());
  }
  return groups.join(' ');
}

export function AuthorityLandingPage() {
  const { authorityKeyId } = useParams<{ authorityKeyId: string }>();
  const navigate = useNavigate();

  // Fetch authority extended info (includes profile data)
  const {
    data: authority,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['authority', authorityKeyId],
    queryFn: () => getAuthorityExtendedInfo(authorityKeyId!),
    enabled: !!authorityKeyId,
    retry: 1,
  });

  // Fetch available processes
  const {
    data: processes,
    isLoading: processesLoading,
  } = useQuery({
    queryKey: ['processes', authorityKeyId],
    queryFn: () => getProcessesForAuthority(authorityKeyId!),
    enabled: !!authorityKeyId && !!authority && !authority.isRevoked,
  });

  // Redirect if key is revoked
  useEffect(() => {
    if (authority?.isRevoked) {
      // Key is revoked, show error state
    }
  }, [authority]);

  const handleSelectProcess = (processKey: string) => {
    navigate(`/submit/${authorityKeyId}/${processKey}`);
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          p: 3,
        }}
      >
        <CircularProgress />
        <Typography sx={{ mt: 2 }} color="text.secondary">
          Behördeninformationen werden geladen...
        </Typography>
      </Box>
    );
  }

  if (error || !authority) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          p: 3,
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 500 }}>
          <Typography variant="h6" gutterBottom>
            Behörde nicht gefunden
          </Typography>
          <Typography>
            Der angegebene Link ist ungültig oder die Behörde ist nicht mehr registriert.
            Bitte wenden Sie sich an Ihre Behörde, um einen gültigen Link zu erhalten.
          </Typography>
        </Alert>
      </Box>
    );
  }

  if (authority.isRevoked) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          p: 3,
        }}
      >
        <Alert severity="warning" sx={{ maxWidth: 500 }}>
          <Typography variant="h6" gutterBottom>
            Schlüssel widerrufen
          </Typography>
          <Typography>
            Der Schlüssel dieser Behörde wurde widerrufen.
            <br />
            <br />
            Bitte wenden Sie sich an die Behörde, um einen aktuellen Link zu erhalten.
          </Typography>
        </Alert>
      </Box>
    );
  }

  // Check if there's any contact info to display
  const hasContactInfo = authority.contactEmail || authority.phone || authority.website;
  const hasAddressInfo = authority.streetAddress || authority.city;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        p: 3,
        bgcolor: 'background.default',
      }}
    >
      <Card sx={{ maxWidth: 600, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <VerifiedIcon color="success" sx={{ fontSize: 40, mr: 2 }} />
            <Box>
              <Typography variant="h5" component="h1">
                {authority.organizationName || authority.ownerName}
              </Typography>
              {authority.department && (
                <Typography variant="body2" color="text.secondary">
                  {authority.department}
                </Typography>
              )}
              <Chip
                label="Verifizierte Behörde"
                color="success"
                size="small"
                sx={{ mt: 0.5 }}
              />
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Contact and Address Info */}
          {(hasContactInfo || hasAddressInfo) && (
            <Box sx={{ mb: 3 }}>
              {hasAddressInfo && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                  <LocationIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                  <Box>
                    {authority.streetAddress && (
                      <Typography variant="body2">{authority.streetAddress}</Typography>
                    )}
                    {authority.city && (
                      <Typography variant="body2">
                        {authority.postalCode && `${authority.postalCode} `}{authority.city}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
              {authority.contactEmail && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <EmailIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                  <Typography
                    variant="body2"
                    component="a"
                    href={`mailto:${authority.contactEmail}`}
                    sx={{ color: 'primary.main', textDecoration: 'none' }}
                  >
                    {authority.contactEmail}
                  </Typography>
                </Box>
              )}
              {authority.phone && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <PhoneIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                  <Typography
                    variant="body2"
                    component="a"
                    href={`tel:${authority.phone}`}
                    sx={{ color: 'primary.main', textDecoration: 'none' }}
                  >
                    {authority.phone}
                  </Typography>
                </Box>
              )}
              {authority.website && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <WebsiteIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                  <Typography
                    variant="body2"
                    component="a"
                    href={authority.website.startsWith('http') ? authority.website : `https://${authority.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: 'primary.main', textDecoration: 'none' }}
                  >
                    {authority.website}
                  </Typography>
                </Box>
              )}
              <Divider sx={{ mt: 2 }} />
            </Box>
          )}

          {/* Info */}
          <Typography variant="body1" color="text.secondary" paragraph>
            Sie sind dabei, einen verschlüsselten Antrag an diese Behörde zu senden.
            Ihre Daten werden Ende-zu-Ende verschlüsselt und können nur von der Behörde
            entschlüsselt werden.
          </Typography>

          {/* Security Info */}
          <Box
            sx={{
              bgcolor: 'success.light',
              borderRadius: 2,
              p: 2,
              mb: 3,
              display: 'flex',
              alignItems: 'flex-start',
            }}
          >
            <SecurityIcon sx={{ color: 'success.dark', mr: 1.5, mt: 0.5 }} />
            <Box>
              <Typography variant="subtitle2" sx={{ color: 'success.dark' }}>
                Ende-zu-Ende-Verschlüsselung
              </Typography>
              <Typography variant="body2" sx={{ color: 'success.dark' }}>
                Ihre Daten werden in Ihrem Browser verschlüsselt, bevor sie gesendet werden.
                Nur die Behörde kann sie entschlüsseln.
              </Typography>
            </Box>
          </Box>

          {/* Fingerprint */}
          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
            Schlüssel-Fingerprint zur Verifikation:
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'monospace',
              bgcolor: 'action.hover',
              p: 1,
              borderRadius: 1,
              wordBreak: 'break-all',
              mb: 3,
            }}
          >
            {formatFingerprint(authority.fingerprint)}
          </Typography>

          {/* Process Selection */}
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" gutterBottom>
            Verfügbare Anträge
          </Typography>

          {processesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : processes && processes.length > 0 ? (
            <List disablePadding>
              {processes.map((process) => (
                <ListItem key={process.key} disablePadding sx={{ mb: 1 }}>
                  <ListItemButton
                    onClick={() => handleSelectProcess(process.key)}
                    sx={{
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      '&:hover': {
                        bgcolor: 'action.hover',
                      },
                    }}
                  >
                    <ListItemIcon>
                      <ProcessIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={process.name || process.key}
                      secondary={process.description}
                    />
                    <ArrowForwardIcon color="action" />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          ) : (
            <Alert severity="info">
              Derzeit sind keine Anträge verfügbar.
            </Alert>
          )}

          {authority.createdAt && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              textAlign="center"
              sx={{ mt: 2 }}
            >
              Registriert seit: {new Date(authority.createdAt).toLocaleDateString('de-DE')}
            </Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
